import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Spinner, Alert, Container } from "react-bootstrap";
import {
  getAlertProfileById,
  AlertProfile,
} from "../../../services/alertProfilesService";
import SmartFiltersWizard from "./SmartFiltersWizard";

const SmartFiltersEditPage: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id?: string }>();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [profile, setProfile] = useState<AlertProfile | null>(null);

  useEffect(() => {
    const loadProfile = async () => {
      if (!id) {
        setError("Profile ID is required");
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);
        const response = await getAlertProfileById(id);
        if (response.success && response.data) {
          // Only allow editing smart profiles
          if (response.data.mode !== "smart") {
            setError("Only smart filter profiles can be edited on this page");
            setLoading(false);
            return;
          }
          setProfile(response.data);
        } else {
          setError("Profile not found");
        }
      } catch (err: any) {
        setError(err?.response?.data?.message || "Failed to load profile");
        console.error("Error loading profile:", err);
      } finally {
        setLoading(false);
      }
    };

    loadProfile();
  }, [id]);

  const handleComplete = () => {
    navigate("/alerts?refresh=true");
  };

  if (loading) {
    return (
      <Container className="smart-filters-edit-loading-container">
        <div className="smart-filters-edit-loading">
          <Spinner animation="border" role="status">
            <span className="visually-hidden">Loading...</span>
          </Spinner>
        </div>
      </Container>
    );
  }

  if (error) {
    return (
      <Container className="smart-filters-edit-container">
        <Alert variant="danger">
          <Alert.Heading>Error</Alert.Heading>
          <p>{error}</p>
        </Alert>
      </Container>
    );
  }

  if (!profile) {
    return (
      <Container className="smart-filters-edit-container">
        <Alert variant="warning">
          <Alert.Heading>Profile Not Found</Alert.Heading>
          <p>The requested profile could not be found.</p>
        </Alert>
      </Container>
    );
  }

  return <SmartFiltersWizard onComplete={handleComplete} editProfile={profile} />;
};

export default SmartFiltersEditPage;
